import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { FiSave, FiUpload } from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './Manage.css';

const ManageProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fullName: '', designation: '', shortBio: '', bio: '', email: '', phone: '',
    location: '', linkedInUrl: '', githubUrl: '', twitterUrl: '', websiteUrl: '',
    availableFor: '', totalExperience: 0,
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');

  useEffect(() => {
    adminAPI.getProfile()
      .then(res => {
        if (res.success) {
          setForm({
            fullName: res.data.fullName || '', designation: res.data.designation || '',
            shortBio: res.data.shortBio || '', bio: res.data.bio || '',
            email: res.data.email || '', phone: res.data.phone || '',
            location: res.data.location || '', linkedInUrl: res.data.linkedInUrl || '',
            githubUrl: res.data.githubUrl || '', twitterUrl: res.data.twitterUrl || '',
            websiteUrl: res.data.websiteUrl || '', availableFor: res.data.availableFor || '',
            totalExperience: res.data.totalExperience || 0,
          });
          setProfilePhotoUrl(res.data.profilePhotoUrl || '');
        }
      })
      .catch(err => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await adminAPI.updateProfile(form);
      if (res.success) toast.success('Profile updated');

      if (profilePhoto) {
        const photoRes = await adminAPI.uploadPhoto(profilePhoto);
        if (photoRes.success) toast.success('Photo uploaded');
      }
      if (resumeFile) {
        const resumeRes = await adminAPI.uploadResume(resumeFile);
        if (resumeRes.success) toast.success('Resume uploaded');
      }
    } catch (err) { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="manage-page">
      <Helmet><title>Manage Profile | Portfolio CMS</title></Helmet>
      <div className="manage-header">
        <h2>Profile Settings</h2>
      </div>

      <div className="card" style={{padding: '30px', maxWidth: '800px'}}>
        <form onSubmit={handleSubmit}>
          <div style={{display:'flex', gap:'30px', alignItems:'center', marginBottom:'30px'}}>
            <div style={{textAlign:'center'}}>
              <div style={{width:120, height:120, borderRadius:'50%', background:'var(--bg-secondary)', overflow:'hidden', marginBottom:10, border:'3px solid var(--primary)'}}>
                {profilePhotoUrl ? <img src={profilePhotoUrl} alt="Profile" style={{width:'100%', height:'100%', objectFit:'cover'}} /> : <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2.5rem', color:'var(--text-muted)'}}>?</div>}
              </div>
              <label className="btn btn-outline" style={{fontSize:'0.8rem', padding:'6px 16px', cursor:'pointer'}}>
                <FiUpload /> Photo
                <input type="file" hidden accept="image/*" onChange={e => setProfilePhoto(e.target.files[0])} />
              </label>
            </div>
            <div>
              <h3 style={{marginBottom:'5px'}}>{form.fullName || 'Your Name'}</h3>
              <p style={{color:'var(--text-secondary)', marginBottom:'10px'}}>{form.designation || 'Your Designation'}</p>
              <label className="btn btn-outline" style={{fontSize:'0.8rem', padding:'6px 16px', cursor:'pointer'}}>
                <FiUpload /> Resume PDF
                <input type="file" hidden accept=".pdf" onChange={e => setResumeFile(e.target.files[0])} />
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group"><label>Full Name</label><input value={form.fullName} onChange={e => setForm({...form, fullName: e.target.value})} required /></div>
            <div className="form-group"><label>Designation</label><input value={form.designation} onChange={e => setForm({...form, designation: e.target.value})} required /></div>
          </div>
          <div className="form-group"><label>Short Bio</label><textarea rows="2" value={form.shortBio} onChange={e => setForm({...form, shortBio: e.target.value})} /></div>
          <div className="form-group"><label>Full Bio</label><textarea rows="5" value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} /></div>

          <h3 style={{fontSize:'1rem', marginTop:20, marginBottom:12, color:'var(--text-secondary)'}}>Contact Information</h3>
          <div className="form-row">
            <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
            <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Location</label><input value={form.location} onChange={e => setForm({...form, location: e.target.value})} /></div>
            <div className="form-group"><label>Available For</label><input value={form.availableFor} onChange={e => setForm({...form, availableFor: e.target.value})} /></div>
          </div>
          <div className="form-group"><label>Total Experience (Years)</label><input type="number" value={form.totalExperience} onChange={e => setForm({...form, totalExperience: parseInt(e.target.value)})} /></div>

          <h3 style={{fontSize:'1rem', marginTop:20, marginBottom:12, color:'var(--text-secondary)'}}>Social Links</h3>
          <div className="form-row">
            <div className="form-group"><label>LinkedIn URL</label><input value={form.linkedInUrl} onChange={e => setForm({...form, linkedInUrl: e.target.value})} /></div>
            <div className="form-group"><label>GitHub URL</label><input value={form.githubUrl} onChange={e => setForm({...form, githubUrl: e.target.value})} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Twitter URL</label><input value={form.twitterUrl} onChange={e => setForm({...form, twitterUrl: e.target.value})} /></div>
            <div className="form-group"><label>Website URL</label><input value={form.websiteUrl} onChange={e => setForm({...form, websiteUrl: e.target.value})} /></div>
          </div>

          <div style={{marginTop: 24}}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <FiSave /> {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManageProfile;
